import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ExportDataDropDown from '@/components/molecules/ExportDataDropDown';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import type { OverviewCage, OverviewLocation } from '@/domain/client';
import { isBwBioscopeVersion } from '@/utils/bioscopeVersionHelpers';
import { toDateString } from '@/utils/date';
import { formatMachineNumber } from '@/utils/localeFormatters';

/**
 * The pen's wounds as its overview cell shows them: BW welfare on a Bioscope 1.0 pen, the Health
 * estimate on every later one.
 */
function woundsOf(cage: OverviewCage) {
    return isBwBioscopeVersion(cage.bioscopeVersion) ? cage.cageData.welfare : cage.cageData.health;
}

/** The region's latest pen measurements as a file to download. */
export default function RegionOverviewDownload({ locations }: { locations: OverviewLocation[] }) {
    const { t } = useTranslation();
    const allData = useMemo(
        () =>
            locations?.flatMap((el) => {
                return el.cages.flatMap((x) => {
                    const weight = x.cageData.weight;
                    const wounds = woundsOf(x);
                    const lice = x.cageData.lice;
                    const swimSpeed = x.cageData.swimSpeed;
                    const dataMin = {
                        LocationName: el?.locationName,
                        PenName: x?.cageName,
                        LastMeasDateForWeight: toDateString(weight?.day),
                        Weight: weight?.livingWeight
                            ? `${formatMachineNumber(weight.livingWeight, 2)} g`
                            : '',
                        Growth: weight?.growth
                            ? `${formatMachineNumber(weight.growth, 2)} g/day`
                            : '',
                        BioscopeStatus: weight?.measStatusList?.[0]
                            ? t(`measurementStatusTexts.${weight.measStatusList[0]}`)
                            : t('common.labels.allGood'),
                        ActiveBioscope: x?.activeBioscopes[0] ?? '',
                    };
                    const dataWithFishHealth = {
                        LastMeasDateForWounds: toDateString(wounds?.day),
                        Wounds: formatMachineNumber(wounds?.woundRate, 2) ?? '',
                    };
                    const dataWithLice = {
                        LastMeasDateForLice: toDateString(lice?.day),
                        FemaleLice: formatMachineNumber(lice?.femaleLegacyAvg, 2) ?? '',
                    };
                    const dataWithSwimSpeed = {
                        SwimSpeed: formatMachineNumber(swimSpeed?.swimSpeed, 2) ?? '',
                    };
                    return {
                        ...dataMin,
                        ...dataWithFishHealth,
                        ...dataWithLice,
                        ...dataWithSwimSpeed,
                    };
                });
            }) ?? [],
        [locations, t]
    );

    const dataColumnNames = allData.length > 0 ? Object.keys(allData[0]) : [];
    const formattedDataColumnNames = dataColumnNames.map((e) => `day.${e}`);
    return (
        <>
            <ExportDataDropDown>
                <DownloadOptions
                    data={allData}
                    columns={formattedDataColumnNames}
                    nameToInclude={'SiteOverview'}
                />
            </ExportDataDropDown>
        </>
    );
}
